import type { Ref } from 'vue';
import type { CustomerAddressFragment } from '~~/lib/Shop/generated/schema';
import { useCustomerAccountStore } from '~/stores/customerAccountStore/customerAccountStore';

function formatAddress(
  address: CustomerAddressFragment | null
): string | undefined {
  if (!address) {
    return undefined;
  }

  const lines: string[] = [];

  if (address.company) {
    lines.push(address.company);
  }

  const name = [address.firstname, address.lastname].filter(Boolean).join(' ');
  if (name) {
    lines.push(name);
  }

  if (address.street?.length) {
    const streetLines = address.street.filter(Boolean) as string[];
    lines.push(...streetLines);
  }

  const cityLine = [address.postcode, address.city].filter(Boolean).join(' ');
  if (cityLine) {
    lines.push(cityLine);
  }

  if (address.country_code) {
    lines.push(address.country_code);
  }

  return lines.length > 0 ? lines.join('\n') : undefined;
}

function setFieldValue(
  container: HTMLElement,
  fieldName: string,
  value: string | undefined
) {
  if (!value) {
    return;
  }

  const field = container.querySelector<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >(`[name="${fieldName}"]`);

  if (!field || field.value) {
    return;
  }

  field.value = value;
}

function blurActiveFormField(container: HTMLElement) {
  const active = document.activeElement;
  if (
    active instanceof HTMLElement &&
    container.contains(active) &&
    (active instanceof HTMLInputElement ||
      active instanceof HTMLTextAreaElement ||
      active instanceof HTMLSelectElement)
  ) {
    active.blur();
  }
}

/**
 * Prefills form fields inside Contentful CustomHTML blocks with the
 * logged-in user's identity (OIDC) and Magento address data.
 *
 * Skips fields that already have a value (e.g. set via URL params).
 */
export function useCtfFormPrefill(containerRef: Ref<HTMLElement | undefined>) {
  if (!import.meta.client) {
    return;
  }

  const { isAuthenticated, user } = useShopAuth();
  const customerAccountStore = useCustomerAccountStore();

  async function prefill() {
    await nextTick();

    const container = containerRef.value;
    if (!container || !isAuthenticated.value) {
      return;
    }

    const forms = container.querySelectorAll('form');
    if (forms.length === 0) {
      return;
    }

    try {
      const userInfo = user.value?.userInfo as
        Record<string, unknown> | undefined;
      const claims = user.value?.claims as Record<string, unknown> | undefined;

      const company = (claims?.Company as string) ?? undefined;
      const fullName = (userInfo?.name as string) ?? undefined;
      const email = (userInfo?.email as string) ?? undefined;

      setFieldValue(container, 'company', company);
      setFieldValue(container, 'name', fullName);
      setFieldValue(container, 'email', email);

      try {
        await customerAccountStore.loadAddresses();
      } catch {
        return;
      }

      const billingAddress = customerAccountStore.defaultBillingAddress;
      const shippingAddress = customerAccountStore.defaultDeliveryAddress;

      const phone = billingAddress?.telephone ?? undefined;

      setFieldValue(container, 'phone', phone);
      setFieldValue(
        container,
        '00N1v00000Slhzp',
        formatAddress(billingAddress)
      );
      setFieldValue(
        container,
        '00N1v00000Slhzk',
        formatAddress(shippingAddress)
      );
    } finally {
      blurActiveFormField(container);
    }
  }

  onMounted(async () => {
    if (isAuthenticated.value) {
      await prefill();
    }
  });

  watch(isAuthenticated, async (isShopAuthenticated) => {
    if (isShopAuthenticated) {
      await prefill();
    }
  });
}
